import React, { useState, useEffect } from 'react';
import { Heart, Phone, X, Plus } from 'lucide-react';
import { useJournalStore } from '../../store/journalStore';
import { usePartsStore } from '../../store/partsStore';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../auth/AuthProvider';

const SUPPORT_MESSAGES = [
  "Take a deep breath. You're doing the best you can.",
  "This moment will pass. You are safe and supported.",
  "Your feelings are valid. It's okay to take space for yourself.",
  "Remember your strength. You've overcome difficult moments before.",
  "All your parts are welcome here. Each one is trying to help.",
  "You're not alone. It's okay to need support.",
  "Trust your journey. Every step counts, no matter how small.",
  "Your awareness is already a form of healing."
];

const EMERGENCY_RESOURCES = [
  {
    name: "988 Suicide & Crisis Lifeline",
    phone: "988",
    description: "24/7 support for mental health crises",
    url: "https://988lifeline.org"
  },
  {
    name: "Crisis Text Line",
    phone: "Text HOME to 741741",
    description: "24/7 crisis support via text",
    url: "https://www.crisistextline.org"
  },
  {
    name: "SAMHSA National Helpline",
    phone: "1-800-662-4357",
    description: "Treatment referral and information",
    url: "https://www.samhsa.gov/find-help/national-helpline"
  }
];

// Pattern types must match database enum exactly
const BREATHING_PATTERNS = [
  {
    id: "4-7-8",
    displayName: "4-7-8 Breathing",
    description: "Inhale for 4, hold for 7, exhale for 8",
    inhale: 4,
    hold: 7,
    exhale: 8
  },
  {
    id: "box",
    displayName: "Box Breathing",
    description: "Equal counts of 4 for inhale, hold, exhale, and rest",
    inhale: 4,
    hold: 4,
    exhale: 4,
    rest: 4
  },
  {
    id: "5-5-5",
    displayName: "5-5-5 Breathing",
    description: "Simple equal breaths for deep relaxation",
    inhale: 5,
    hold: 5,
    exhale: 5
  },
  {
    id: "calming",
    displayName: "Calming Breath",
    description: "Long exhale for enhanced relaxation",
    inhale: 4,
    hold: 2,
    exhale: 6
  }
];

export default function ExhaleButton() {
  const { user } = useAuth();
  const [showSupport, setShowSupport] = useState(false);
  const [showEmergency, setShowEmergency] = useState(false);
  const [message, setMessage] = useState('');
  const [isBreathing, setIsBreathing] = useState(false);
  const [breathingPattern, setBreathingPattern] = useState(BREATHING_PATTERNS[0]);
  const [phase, setPhase] = useState<'inhale' | 'hold' | 'exhale' | 'rest'>('inhale');
  const [counter, setCounter] = useState(4);
  const [sessionId, setSessionId] = useState<string | null>(null);
  
  const { entries } = useJournalStore();
  const { parts } = usePartsStore();

  const getContextualMessage = () => {
    const recentEntries = entries.slice(0, 5);
    const hasPartWork = parts.length > 0;
    const hasIntegrationWork = recentEntries.some(e => e.shadow_work_type === 'integration');

    let messages = [...SUPPORT_MESSAGES];
    if (hasPartWork) {
      messages = messages.filter(m => m.includes('parts') || m.includes('journey'));
    }
    if (hasIntegrationWork) {
      messages = messages.filter(m => m.includes('healing') || m.includes('strength'));
    }

    const randomIndex = Math.floor(Math.random() * messages.length);
    return messages[randomIndex];
  };

  const handleExhale = () => {
    setMessage(getContextualMessage());
    setShowSupport(true);
    setShowEmergency(false);
    setIsBreathing(false);
  };

  // Start breathing session
  const startBreathingSession = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('breathing_sessions')
        .insert([{
          user_id: user.id,
          pattern_type: breathingPattern.id, // Use pattern ID that matches database enum
          started_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;
      setSessionId(data.id);
    } catch (err) {
      console.error('Error starting breathing session:', err);
    }
  };

  // End breathing session
  const endBreathingSession = async () => {
    if (!sessionId || !user) return;

    try {
      await supabase
        .from('breathing_sessions')
        .update({
          completed: true,
          completed_at: new Date().toISOString()
        })
        .eq('id', sessionId)
        .eq('user_id', user.id);
    } catch (err) {
      console.error('Error ending breathing session:', err);
    }
  };

  // Breathing exercise timer
  useEffect(() => {
    let timer: number;
    
    if (isBreathing && counter > 0) {
      timer = window.setInterval(() => {
        setCounter(prev => prev - 1);
      }, 1000);
    } else if (counter === 0) {
      // Transition to next phase
      switch (phase) {
        case 'inhale':
          setPhase('hold');
          setCounter(breathingPattern.hold);
          break;
        case 'hold':
          setPhase('exhale');
          setCounter(breathingPattern.exhale);
          break;
        case 'exhale':
          if (breathingPattern.rest) {
            setPhase('rest');
            setCounter(breathingPattern.rest);
          } else {
            setPhase('inhale');
            setCounter(breathingPattern.inhale);
          }
          break;
        case 'rest':
          setPhase('inhale');
          setCounter(breathingPattern.inhale);
          break;
      }
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isBreathing, counter, phase, breathingPattern]);

  // Clean up when support modal is closed
  useEffect(() => {
    if (!showSupport) {
      setIsBreathing(false);
      setPhase('inhale');
      setCounter(breathingPattern.inhale);
      if (sessionId) {
        endBreathingSession();
        setSessionId(null);
      }
    }
  }, [showSupport, breathingPattern.inhale, sessionId]);

  return (
    <>
      <motion.button
        onClick={handleExhale}
        className="fixed bottom-6 right-6 flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-marigold-500 text-white rounded-full hover:from-amber-400 hover:to-marigold-400 transition-all shadow-lg z-50"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <Heart className="h-5 w-5" />
        <span>Exhale</span>
      </motion.button>

      {/* Support Modal */}
      <AnimatePresence mode="wait">
        {showSupport && (
          <motion.div
            key="support-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-midnight-900/90 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full"
            >
              <div className="p-6">
                <button
                  onClick={() => setShowSupport(false)}
                  className="absolute top-4 right-4 text-midnight-400 hover:text-midnight-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>

                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  className="flex justify-center mb-6"
                >
                  <Heart className="h-12 w-12 text-amber-500" />
                </motion.div>

                {isBreathing ? (
                  <motion.div
                    key="breathing-exercise"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-center space-y-6"
                  >
                    <h3 className="text-xl font-medium text-midnight-800">
                      {breathingPattern.displayName}
                    </h3>
                    <div className="relative w-full aspect-video flex items-center justify-center">
                      <motion.div
                        className="w-32 h-32 rounded-full bg-gradient-to-r from-amber-500 to-marigold-500 mx-auto"
                        animate={{
                          scale: phase === 'inhale' ? 1.5 :
                                phase === 'hold' ? 1.5 :
                                phase === 'exhale' ? 1 : 1
                        }}
                        transition={{ duration: phase === 'inhale' ? breathingPattern.inhale :
                                              phase === 'exhale' ? breathingPattern.exhale : 0.5 }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center text-white text-2xl font-bold">
                        {counter}
                      </div>
                    </div>
                    <p className="text-lg text-midnight-600">
                      {phase === 'inhale' ? 'Breathe In...' :
                       phase === 'hold' ? 'Hold...' :
                       phase === 'exhale' ? 'Breathe Out...' : 'Rest...'}
                    </p>
                  </motion.div>
                ) : (
                  <motion.div
                    key="support-content"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <p className="text-xl text-center text-midnight-800 mb-8">
                      {message}
                    </p>

                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        {BREATHING_PATTERNS.map((pattern, index) => (
                          <motion.button
                            key={pattern.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ 
                              opacity: 1, 
                              y: 0,
                              transition: { delay: index * 0.1 }
                            }}
                            onClick={() => {
                              setBreathingPattern(pattern);
                              setIsBreathing(true);
                              setPhase('inhale');
                              setCounter(pattern.inhale);
                              startBreathingSession();
                            }}
                            className="p-3 rounded-lg border-2 border-mint-200 hover:border-amber-500 hover:bg-amber-50 transition-all text-left"
                          >
                            <h4 className="font-medium text-midnight-800">{pattern.displayName}</h4>
                            <p className="text-sm text-midnight-600">{pattern.description}</p>
                          </motion.button>
                        ))}
                      </div>

                      <div className="mt-6 flex justify-center">
                        <button
                          onClick={() => setShowEmergency(true)}
                          className="text-sm text-midnight-500 hover:text-midnight-700 transition-colors"
                        >
                          I need immediate support
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Emergency Modal */}
        {showEmergency && (
          <motion.div
            key="emergency-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-midnight-900/90 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full"
            >
              <div className="p-6">
                <button
                  onClick={() => setShowEmergency(false)}
                  className="absolute top-4 right-4 text-midnight-400 hover:text-midnight-600"
                >
                  <X className="h-5 w-5" />
                </button>

                <div className="flex items-center space-x-3 mb-6">
                  <Phone className="h-6 w-6 text-amber-500" />
                  <h2 className="text-xl font-semibold text-midnight-800">
                    24/7 Support Resources
                  </h2>
                </div>

                <div className="space-y-4">
                  {EMERGENCY_RESOURCES.map((resource, index) => (
                    <motion.div
                      key={resource.name}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ 
                        opacity: 1, 
                        x: 0,
                        transition: { delay: index * 0.1 }
                      }}
                      className="p-4 bg-mint-50 rounded-lg border border-mint-200"
                    >
                      <h3 className="font-medium text-midnight-800 mb-1">
                        {resource.name}
                      </h3>
                      <p className="text-midnight-600 text-sm mb-2">
                        {resource.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-amber-600">
                          {resource.phone}
                        </span>
                        <a
                          href={resource.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-1 text-sm text-mint-600 hover:text-mint-700"
                        >
                          <span>Learn More</span>
                          <Plus className="h-4 w-4" />
                        </a>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}